import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { 
  Edit2, Trash2, Archive, RotateCcw, CheckCircle, Bell, 
  ChevronsUp, ChevronsDown
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ArchivedParentBadge from '@/components/common/ArchivedParentBadge';
import StatusFlagButtons from '@/components/common/StatusFlagButtons';
import { calculateLevel } from '@/components/hooks/useOfficerUtils';

export default function ObjectiveCard({
  objective,
  mission,
  officers,
  activities = [],
  dailyTasks = [],
  user,
  profile,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  onUpdateStatusFlag,
  onActivate,
  onPingCommanders,
  canActivate
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const accountable = officers.find(o => o.user_email === objective.owner_email);
  const isOwner = objective.owner_email === user?.email;
  const isCiC = profile?.custom_role === 'Commander in Chief';
  const canManage = isOwner || isCiC;
  
  // Calculate activity/task stats
  const objActivities = activities.filter(a => a.objective_id === objective.id && a.status === 'active');
  const objTasks = dailyTasks.filter(t => {
    const activity = activities.find(a => a.id === t.activity_id);
    return activity && activity.objective_id === objective.id && !t.completed;
  });
  
  const actStatusCounts = {
    green: objActivities.filter(a => a.key_results_status === 'green').length,
    yellow: objActivities.filter(a => a.key_results_status === 'yellow').length,
    red: objActivities.filter(a => a.key_results_status === 'red').length
  };
  
  const taskStatusCounts = {
    green: objTasks.filter(t => t.status_flag === 'green').length,
    yellow: objTasks.filter(t => t.status_flag === 'yellow').length,
    red: objTasks.filter(t => t.status_flag === 'red').length
  };
  
  const actTotal = objActivities.length;
  const taskTotal = objTasks.length;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-50 text-slate-700 p-6 rounded-xl border border-slate-800 hover:border-indigo-500/30 transition-colors"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {/* Status Flags */}
            {objective.status === 'active' && (
              <StatusFlagButtons
                currentFlag={objective.status_flag}
                onChange={(flag) => onUpdateStatusFlag(objective.id, flag)}
                size="sm"
              />
            )}
            
            {mission?.status === 'archived' && (
              <ArchivedParentBadge 
                parentType="Mission"
                parentName={mission.title}
                parentPage="missions"
              />
            )}
            
            <Badge className={
              objective.status === 'active' ? 'bg-emerald-500/20 text-emerald-400 border-none' :
              objective.status === 'pending_activation' ? 'bg-amber-500/20 text-amber-400 border-none' :
              objective.status === 'completed' ? 'bg-slate-500/20 text-slate-400 border-none' :
              'bg-blue-500/20 text-blue-400 border-none'
            }>
              {objective.status === 'pending_activation' ? 'Pending Activation' : objective.status}
            </Badge>
            
            {objective.specific && objective.measurable && objective.attainable &&
             objective.relevant && objective.time_bound &&
              <Badge variant="outline" className="text-emerald-400 border-emerald-400/30">
                <CheckCircle className="w-3 h-3 mr-1" />
                SMART Complete
              </Badge>
            }
            {objective.bridge_quality_score !== undefined && (
              <Badge variant="outline" className="text-indigo-400 border-indigo-400/30">
                Bridge: {Math.round(objective.bridge_quality_score)}%
              </Badge>
            )}
            {objective.smart_completeness_score !== undefined && (
              <Badge variant="outline" className="text-cyan-400 border-cyan-400/30">
                SMART: {Math.round(objective.smart_completeness_score)}%
              </Badge>
            )}
          </div>

          <h3 className="text-slate-700 mb-2 text-lg font-semibold">
            {objective.objective_code && <span className="text-indigo-500 mr-2">[{objective.objective_code}]</span>}
            {objective.statement}
          </h3>
          
          {accountable && (
            <div className="text-xs text-slate-500 mb-3">
              Accountable: {accountable.rank_title} {accountable.callsign} • Lvl {calculateLevel(accountable.total_xp)}
            </div>
          )}
          
          {/* Activity/Tasks Bars */}
          {objective.status === 'active' && (actTotal > 0 || taskTotal > 0) && (
            <div className="mb-3 space-y-2">
              {actTotal > 0 && (
                <div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>Activities ({actTotal})</span>
                    <div className="flex gap-2">
                      {actStatusCounts.green > 0 && <span className="text-emerald-400">{actStatusCounts.green} 🟢</span>}
                      {actStatusCounts.yellow > 0 && <span className="text-amber-400">{actStatusCounts.yellow} 🟡</span>}
                      {actStatusCounts.red > 0 && <span className="text-red-400">{actStatusCounts.red} 🔴</span>}
                    </div>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden flex">
                    {actStatusCounts.green > 0 && <div className="bg-emerald-500" style={{ width: `${(actStatusCounts.green / actTotal) * 100}%` }} />}
                    {actStatusCounts.yellow > 0 && <div className="bg-amber-500" style={{ width: `${(actStatusCounts.yellow / actTotal) * 100}%` }} />}
                    {actStatusCounts.red > 0 && <div className="bg-red-500" style={{ width: `${(actStatusCounts.red / actTotal) * 100}%` }} />}
                  </div>
                </div>
              )}
              {taskTotal > 0 && (
                <div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>Tasks ({taskTotal})</span>
                    <div className="flex gap-2">
                      {taskStatusCounts.green > 0 && <span className="text-emerald-400">{taskStatusCounts.green} 🟢</span>}
                      {taskStatusCounts.yellow > 0 && <span className="text-amber-400">{taskStatusCounts.yellow} 🟡</span>}
                      {taskStatusCounts.red > 0 && <span className="text-red-400">{taskStatusCounts.red} 🔴</span>}
                    </div>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden flex">
                    {taskStatusCounts.green > 0 && <div className="bg-emerald-500" style={{ width: `${(taskStatusCounts.green / taskTotal) * 100}%` }} />}
                    {taskStatusCounts.yellow > 0 && <div className="bg-amber-500" style={{ width: `${(taskStatusCounts.yellow / taskTotal) * 100}%` }} />}
                    {taskStatusCounts.red > 0 && <div className="bg-red-500" style={{ width: `${(taskStatusCounts.red / taskTotal) * 100}%` }} />}
                  </div>
                </div>
              )}
              
              {/* Key Results */}
              {objActivities.map(activity => {
                if (!activity.key_results || activity.key_results.length === 0) return null;
                const responsible = officers.find(o => o.user_email === activity.responsible_email);
                const level = responsible ? calculateLevel(responsible.total_xp) : 0;
                
                return activity.key_results.map((kr, idx) => {
                  const current = parseFloat(kr.current_value) || 0;
                  const target = parseFloat(kr.target) || 1;
                  const progress = Math.min((current / target) * 100, 100);
                  
                  return (
                    <div key={`${activity.id}-${idx}`} className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-600 flex-1 truncate">{kr.description}</span>
                        <span className="text-slate-500 ml-2">
                          {activity.title} • {responsible ? `${responsible.rank_title} ${responsible.callsign} Lvl ${level}` : activity.responsible_email}
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${progress >= 100 ? 'bg-emerald-500' : progress >= 50 ? 'bg-cyan-500' : 'bg-amber-500'}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {kr.current_value}/{kr.target} {kr.unit}
                      </div>
                    </div>
                  );
                });
              })}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          {/* Collapse/Expand Button */}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-8 w-8 text-slate-500 hover:text-indigo-400"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <ChevronsUp className="w-4 h-4" /> : <ChevronsDown className="w-4 h-4" />}
          </Button>

          {canManage && (
            <>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onEdit(objective)}
                className="h-8 w-8 text-slate-500 hover:text-indigo-400"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </Button>

              {objective.status === 'archived' ? (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onUnarchive(objective.id)}
                  className="h-8 w-8 text-slate-500 hover:text-emerald-400"
                  title="Unarchive"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onArchive(objective)}
                  className="h-8 w-8 text-slate-500 hover:text-amber-400"
                  title="Archive"
                >
                  <Archive className="w-4 h-4" />
                </Button>
              )}

              <Button
                size="icon"
                variant="ghost"
                onClick={() => onDelete(objective)}
                className="h-8 w-8 text-slate-500 hover:text-rose-400"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            {/* SMART Details */}
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              {objective.specific &&
                <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-green-400 font-semibold">
                      Specific{objective.smart_specific_score > 0 && ` - ${objective.smart_specific_score * 20}%`}
                    </span>
                    {objective.smart_specific_score > 0 && (
                      <Badge variant="outline" className={`text-xs h-5 ${objective.smart_specific_score >= 4 ? 'text-emerald-400 border-emerald-500/30' : 'text-amber-400 border-amber-500/30'}`}>
                        {objective.smart_specific_score}/5
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-300">{objective.specific}</p>
                </div>
              }
              {objective.measurable &&
                <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-green-400 font-semibold">
                      Measurable{objective.smart_measurable_score > 0 && ` - ${objective.smart_measurable_score * 20}%`}
                    </span>
                    {objective.smart_measurable_score > 0 && (
                      <Badge variant="outline" className={`text-xs h-5 ${objective.smart_measurable_score >= 4 ? 'text-emerald-400 border-emerald-500/30' : 'text-amber-400 border-amber-500/30'}`}>
                        {objective.smart_measurable_score}/5
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-300">{objective.measurable}</p>
                </div>
              }
              {objective.attainable &&
                <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-green-400 font-semibold">
                      Attainable{objective.smart_attainable_score > 0 && ` - ${objective.smart_attainable_score * 20}%`}
                    </span>
                    {objective.smart_attainable_score > 0 && (
                      <Badge variant="outline" className={`text-xs h-5 ${objective.smart_attainable_score >= 4 ? 'text-emerald-400 border-emerald-500/30' : 'text-amber-400 border-amber-500/30'}`}>
                        {objective.smart_attainable_score}/5
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-300">{objective.attainable}</p>
                </div>
              }
              {objective.relevant &&
                <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-green-400 font-semibold">
                      Relevant{objective.smart_relevant_score > 0 && ` - ${objective.smart_relevant_score * 20}%`}
                    </span>
                    {objective.smart_relevant_score > 0 && (
                      <Badge variant="outline" className={`text-xs h-5 ${objective.smart_relevant_score >= 4 ? 'text-emerald-400 border-emerald-500/30' : 'text-amber-400 border-amber-500/30'}`}>
                        {objective.smart_relevant_score}/5
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-300">{objective.relevant}</p>
                </div>
              }
              {objective.time_bound &&
                <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800 md:col-span-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-green-400 font-semibold">
                      Time-Bound{objective.smart_time_bound_score > 0 && ` - ${objective.smart_time_bound_score * 20}%`}
                    </span>
                    {objective.smart_time_bound_score > 0 && (
                      <Badge variant="outline" className={`text-xs h-5 ${objective.smart_time_bound_score >= 4 ? 'text-emerald-400 border-emerald-500/30' : 'text-amber-400 border-amber-500/30'}`}>
                        {objective.smart_time_bound_score}/5
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-300">{objective.time_bound}</p>
                </div>
              }
            </div>

            {objective.iteration_notes &&
              <div className="mt-3 p-3 bg-indigo-500/5 rounded-lg border border-indigo-500/20">
                <span className="text-slate-500 text-xs font-semibold block mb-1">Iteration Notes</span>
                <p className="text-slate-400 text-sm">{objective.iteration_notes}</p>
              </div>
            }

            {objective.status === 'pending_activation' && objective.activation_requested_by !== user?.email && !canActivate && (
              <div className="mt-4 pt-4 border-t border-slate-800 flex gap-2">
                <div className="text-sm text-amber-400 mb-3">
                  Activation requested by {objective.activation_requested_by} on {format(new Date(objective.activation_requested_at), 'MMM d, yyyy')}
                </div>
              </div>
            )}
            
            {objective.status === 'pending_activation' && (objective.activation_requested_by === user?.email || canActivate) && (
              <div className="mt-4 pt-4 border-t border-slate-800">
                <div className="text-sm text-amber-400 mb-3">
                  Activation requested by {objective.activation_requested_by} on {format(new Date(objective.activation_requested_at), 'MMM d, yyyy')}
                </div>
                <div className="flex gap-2">
                  {canActivate ? (
                    <Button
                      size="sm"
                      onClick={() => onActivate(objective.id)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve & Activate
                    </Button>
                  ) : objective.activation_requested_by === user?.email && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onPingCommanders(objective.id)}
                      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Ping Commanders
                    </Button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}